<template>
  <el-dialog
    class="comp-container"
    :visible.sync="visible"
    width="30%"
    top="30vh"
    custom-class="common-dialog"
    :append-to-body="true"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
  >
    <!-- 标题区域 -->
    <div v-if="title" class="dialog-title">
      {{ title }}
    </div>

    <!-- 内容区域 -->
    <div class="dialog-content">
      <slot name="content">
        <div class="tips">
          {{ content }}
        </div>
      </slot>
    </div>

    <!-- 自定义内容插槽 -->
    <div v-if="$slots.default" class="custom-content">
      <slot></slot>
    </div>

    <!-- 按钮区域 -->
    <div class="footer">
      <slot name="footer" :close="handleClose">
        <el-button
          v-for="(button, index) in buttons"
          :key="index"
          :type="button.type || 'default'"
          :loading="button.loading"
          :disabled="button.disabled"
          @click="handleButtonClick(button, index)"
        >
          {{ button.text }}
        </el-button>
      </slot>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "CommonDialog",
  props: {
    // 弹窗标题
    title: {
      type: String,
      default: "",
    },
    // 弹窗内容
    content: {
      type: String,
      default: "",
    },
    // 按钮配置
    buttons: {
      type: Array,
      default: () => [
        {
          text: "取消",
          type: "default",
          action: "cancel",
        },
        {
          text: "确定",
          type: "primary",
          action: "confirm",
        },
      ],
    },
    // 是否显示弹窗
    value: {
      type: Boolean,
      default: false,
    },
    // 点击遮罩层是否关闭
    closeOnClickModal: {
      type: Boolean,
      default: false,
    },
    // 按ESC键是否关闭
    closeOnPressEscape: {
      type: Boolean,
      default: false,
    },
    // 弹窗宽度
    width: {
      type: String,
      default: "30%",
    },
    // 弹窗顶部距离
    top: {
      type: String,
      default: "30vh",
    },
  },

  data() {
    return {
      visible: false,
    };
  },

  watch: {
    value: {
      handler(newVal) {
        this.visible = newVal;
      },
      immediate: true,
    },
    visible(newVal) {
      this.$emit("input", newVal);
    },
  },

  methods: {
    // 处理按钮点击
    handleButtonClick(button, index) {
      // 如果有自定义处理函数，优先执行
      if (button.handler && typeof button.handler === "function") {
        const result = button.handler(this);
        // 如果返回 false，不关闭弹窗
        if (result === false) {
          return;
        }
      }

      // 触发按钮点击事件
      this.$emit("button-click", {
        button,
        index,
        action: button.action,
      });

      // 根据 action 类型处理
      switch (button.action) {
        case "cancel":
          this.handleCancel();
          break;
        case "confirm":
          this.handleConfirm();
          break;
        case "close":
          this.handleClose();
          break;
        default:
          // 如果没有特殊处理，默认关闭弹窗
          if (button.close !== false) {
            this.handleClose();
          }
      }
    },

    // 取消操作
    handleCancel() {
      this.$emit("cancel");
      this.handleClose();
    },

    // 确认操作
    handleConfirm() {
      this.$emit("confirm");
      this.handleClose();
    },

    // 关闭弹窗
    handleClose() {
      this.visible = false;
      this.$emit("close");
    },

    // 打开弹窗
    open() {
      this.visible = true;
      this.$emit("open");
    },

    // 关闭弹窗
    close() {
      this.visible = false;
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.comp-container ::v-deep {
  .common-dialog {
    width: 327px !important;
    background: #18181b;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.06),
      0px 1px 3px 0px rgba(0, 0, 0, 0.1);
    border-radius: 12px 12px 12px 12px;
    border: 1px solid rgba(229, 231, 235, 0.04);
    padding: 16px;

    .el-dialog__header {
      display: none;
    }

    .el-dialog__body {
      padding: 0;
      font-size: 14px;
      color: #a1a1aa;
      text-align: center;

      .dialog-title {
        font-size: 16px;
        font-weight: 600;
        color: #ecedee;
        margin-bottom: 16px;
        text-align: center;
      }

      .dialog-content {
        margin-bottom: 16px;

        .tips {
          max-width: 300px;
          min-height: 60px;
          display: flex;
          justify-content: center;
          align-items: center;
          line-height: 1.5;
          word-break: break-word;
        }
      }

      .custom-content {
        margin-bottom: 16px;

        .el-checkbox {
          margin-top: 8px;

          .el-checkbox__input.is-checked {
            .el-checkbox__inner {
              background: #165dff;
              border-color: #165dff;
            }
          }

          .el-checkbox__inner {
            border-color: #165dff !important;

            &:hover {
              border-color: #165dff;
            }
          }

          .el-checkbox__label {
            color: #a1a1aa;
          }
        }

        .el-input {
          margin: 8px 0;

          .el-input__inner {
            background: #3f3f46;
            border-color: #525252;
            color: #ecedee;

            &:focus {
              border-color: #165dff;
            }

            &::placeholder {
              color: #71717a;
            }
          }
        }

        .el-textarea {
          margin: 8px 0;

          .el-textarea__inner {
            background: #3f3f46;
            border-color: #525252;
            color: #ecedee;

            &:focus {
              border-color: #165dff;
            }

            &::placeholder {
              color: #71717a;
            }
          }
        }
      }

      .footer {
        margin-top: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .el-button {
          flex: 1;
          background: #165dff;
          border-radius: 12px 12px 12px 12px;
          border: none;
          font-weight: 500;
          font-size: 14px;
          color: #ecedee;
          &.el-button--primary {
            background: #165dff;
            &:hover {
              background: #165dff !important;
            }
            &:focus {
              background: #165dff !important;
            }
          }
          &.el-button--default {
            background: #3f3f46;
          }
        }
      }
    }

    .el-dialog__footer {
      display: none;
    }
  }
}
</style>
